import { Link } from 'react-router-dom'

// ⚠️ Comparatif avec un produit tiers. Tout ce qui est dit de Covenant Eyes
// se limite à son MODÈLE public (accountability : rapports envoyés à un
// « ally »), pas à des prix ni à des fonctionnalités précises qui changent.
// Ne rien ajouter ici qui ne se vérifie pas sur leur site le jour même.
const guide = {
  slug: 'covenant-eyes-alternative',
  category: 'compare',
  kicker: 'Compare',
  title: 'Covenant Eyes alternative: a lock instead of a report',
  seoTitle: 'Covenant Eyes Alternative: A Lock, Not a Report',
  description: `Covenant Eyes is built around a person who receives reports of your activity. If you want the door shut without a witness, here is how the lock model compares.`,
  published: '2026-08-25',
  modified: '2026-09-26',
  minutes: 6,
  intro: `Covenant Eyes is the best-known name in this space, and it earned
          it. It also makes one big assumption — that what you need is a
          witness. If that assumption doesn't fit you, you're not looking
          for a better report. You're looking for a different mechanism.`,
  next: ['porn-blocker-without-accountability-partner', 'porn-blocker-you-cant-uninstall', 'blockerx-alternative'],
  body: (
    <>
      <h2>Two different theories of the problem</h2>
      <p>
        Covenant Eyes is accountability software. Its centre of gravity is
        the <b>ally</b>: a person you name who receives reports about your
        device activity. The idea is that being seen changes behaviour.
        Blocking exists in the product, but the report is the point.
        Features and prices change, so check their site for the current
        details — the model, though, has been stable for years.
      </p>
      <p>
        SELFCNTRL starts from the opposite observation: you don't relapse
        because nobody's watching. You relapse because at two in the
        morning the door is open, and you're not the person who shut it
        this morning. So the product is the door — a filter that can't be
        taken off on impulse — and there is no witness in it at all.
      </p>

      <h2>Where the accountability model fits</h2>
      <p>
        It's honest to say where it works. If you already have a person you
        trust with this, if being seen genuinely steadies you, and if you're
        comfortable with your activity being analysed to produce reports,
        accountability software can carry you. Many people have quit with
        an ally. This isn't a piece that pretends otherwise.
      </p>

      <h2>Where it doesn't</h2>
      <ul>
        <li><b>You'd have to tell someone.</b> For a lot of people that conversation is the wall, and the install never happens. See <Link to="/guides/porn-blocker-without-accountability-partner">quitting without an accountability partner</Link>.</li>
        <li><b>A person is a key.</b> Whatever the ally has to approve — an uninstall, a change — is one message away, on a night you'll send it.</li>
        <li><b>Reports need data.</b> Something has to read your activity to write them. If the promise "nobody reads my history" matters to you, the two models are incompatible by design.</li>
        <li><b>The report arrives after.</b> A report documents a relapse; it doesn't prevent one. The block has to be what prevents it, and a block that leans on the ally to stay installed leans on the weakest hour.</li>
      </ul>

      <h2>What the lock model does instead</h2>
      <ul>
        <li><b>Category filtering at DNS level</b>, on every device you own — iPhone, iPad, Mac, Windows — covering every app, every browser and private mode.</li>
        <li><b>A supervised profile on iPhone and iPad</b> that can't be removed from the phone and forbids adding a VPN. No Remove Profile button, no passcode, no person to ask. Coming off is a deliberate request from your account with a cooling-off delay.</li>
        <li><b>A lock that only tightens.</b> Tightening applies at once, loosening waits twenty-four hours, the floor never drops.</li>
        <li><b>Night curfews</b> that cut the internet entirely on a schedule — the hours where it happens.</li>
        <li><b>No history, no reports, no partner.</b> Filtering is by category; there's nothing to read and nothing to send. Nothing explicit on the bank statement.</li>
        <li><b>$100 if you beat it.</b> Strip the filter off a device we locked, and we pay you on top of a refund — <Link to="/remboursement">conditions here</Link>.</li>
      </ul>

      <h2>Side by side</h2>
      <table>
        <thead>
          <tr><th>Question</th><th>Accountability model</th><th>SELFCNTRL</th></tr>
        </thead>
        <tbody>
          <tr><td>What holds you</td><td>A person who sees reports</td><td>A profile that can't come off</td></tr>
          <tr><td>Have to tell someone</td><td>Yes, by design</td><td>No</td></tr>
          <tr><td>Your activity is read</td><td>Yes, to make reports</td><td>No — category filter, no history</td></tr>
          <tr><td>Removing it at 2am</td><td>Depends on the ally / the platform</td><td>Not possible from the phone (supervised)</td></tr>
          <tr><td>Adding a VPN to get around it</td><td>Depends on the platform</td><td>Forbidden by the profile on iPhone / iPad</td></tr>
          <tr><td>Loosening the block</td><td>—</td><td>Waits 24 h; floor never drops</td></tr>
          <tr><td>Price</td><td>See their site</td><td>$9.99/mo, or $49.99/year</td></tr>
        </tbody>
      </table>

      <h2>Choosing</h2>
      <p>
        If you want to be seen, choose accountability. If you want the door
        shut and no one in the loop, choose the lock — the mechanism, in
        full, is in <Link to="/guides/porn-blocker-you-cant-uninstall">what
        makes a blocker immovable</Link>, and the ten-minute setup is in
        <Link to="/guides/how-to-block-porn-on-iphone"> the iPhone guide</Link>.
        Either way, the thing to avoid is the third option: a blocker you
        can turn off yourself.
      </p>
    </>
  ),
  faq: [
    {
      q: 'Is there a cheaper alternative to Covenant Eyes?',
      a: `Prices change, so compare both sites on the day. SELFCNTRL is $9.99 a
          month or $49.99 a year, and the price
          buys a lock rather than a monitoring service: no second person, no
          reports, nothing read.`
    },
    {
      q: 'Is there something like Covenant Eyes that is free?',
      a: `Free options exist at the filter level: Screen Time on Apple
          devices and public filtering DNS resolvers. They filter; they don't
          hold, because you can switch them off. What you pay for, with
          Covenant Eyes or with us, is the part that makes the block stay.`
    },
    {
      q: 'Is SELFCNTRL an accountability app like Covenant Eyes?',
      a: `No. There is no ally, no report and nothing that reads your
          activity. SELFCNTRL holds through a supervised, non-removable
          profile and a cooling-off delay — a lock, not a witness.`
    },
    {
      q: 'Can I use both?',
      a: `Technically yes; they don't conflict. But they answer different
          needs — if the lock is on, there's nothing left for a report to
          document.`
    },
    {
      q: 'Does SELFCNTRL work on Android?',
      a: `Not today. It covers iPhone, iPad, Mac and Windows. The immovable
          lock relies on Apple's supervision, which is why iPhone and iPad
          are where it started.`
    }
  ]
}

export default guide
